import { useCallback, useMemo } from 'react'
import { Box, Container, Grid, Typography } from '@mui/material'
import parse from 'html-react-parser'

import Form from '@/components/base/form'
import Spacer from '@/components/sections/spacer'
import { useNotification } from '@/contexts/notification'
import { useAppStore } from '@/contexts/store'

import { useAuth } from '@/api/auth'
import { WPCF7FormField, WPPageData } from '@/types'

export type ForgotPasswordProps = {
  pageProps?: Omit<WPPageData, 'acf'> & {
    acf: {
      title?: string
      text?: string
      form?: {
        form: {
          fields: WPCF7FormField[]
          button: {
            text: string
          }
        }
      }
    }
  }
}

const ForgotPassword = ({ pageProps }: ForgotPasswordProps) => {
  const acf = useMemo(() => pageProps?.acf, [pageProps])
  const form = useMemo(() => acf?.form?.form, [acf])
  const fields = useMemo(() => form?.fields || [], [form])
  const { strings } = useAppStore()

  const { forgotPassword } = useAuth()
  const { notifySuccess, notifyError } = useNotification()

  const handleSubmit = useCallback(
    async (values: Record<string, any>) => {
      try {
        // Extract email from form values
        let email = ''
        fields.forEach((field) => {
          const inputKey = `input_${field.id}`
          if (field.adminLabel === 'email' && values[inputKey] !== undefined) {
            email = values[inputKey]
          }
        })

        const response = await forgotPassword({ email })
        if (response?.message) {
          notifySuccess(response.message)
        }

        return true
      } catch (error: any) {
        notifyError(error.message || strings?.forgot_password?.error?.request_failed_fallback || '')
        return false
      }
    },
    [fields, forgotPassword, notifyError, notifySuccess, strings]
  )

  return (
    <>
      <Container>
        <Grid container>
          <Grid size={{ xs: 4, sm: 6 }} offset={{ sm: 1, md: 3 }}>
            <Box
              sx={(theme) => ({
                p: { xs: 3, sm: 4 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                borderRadius: theme.shape.borderRadius4,
                backgroundColor: theme.palette.surface.background,
                boxShadow: theme.shadows[2]
              })}
            >
              {acf?.title && <Typography variant="h4">{acf.title}</Typography>}
              <Spacer size="xxs" />
              {acf?.text && (
                <Typography variant="textRegular" color="text.primary">
                  {parse(acf.text)}
                </Typography>
              )}
              <Spacer size="xs" />
              <Form
                form_id={form?.fields[0]?.formId?.toString() || ''}
                fields={form?.fields || []}
                submit={{
                  text: form?.button.text || strings?.labels?.submit || '',
                  color: 'primary'
                }}
                onSubmit={handleSubmit}
                customEndpoint={true}
                useGravity={false}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Spacer size="2xl" />
    </>
  )
}

export default ForgotPassword
